import { observer } from 'mobx-react';
import Input from '../../../inputs/Input';
import CheckBox from '../../../inputs/CheckBox';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import { COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS } from '../../../../utils/constants/fields';

/**
 * ShareholderFilters - Filter panel for shareholder structure visualization
 *
 * Supports two variants:
 * - "popup": Always visible in full-screen popup, uses "Apply" button text
 * - "mini": Collapsible panel in tab view, uses "Filter" button text
 *
 * Props:
 * @param {object} filters - Current filter values
 * @param {object} appliedFilters - Currently applied filter values
 * @param {object} validationMessages - Validation error messages
 * @param {boolean} showOnSubmitErrorState - Whether to show validation errors
 * @param {boolean} isFilterDisabled - Whether the apply/filter button is disabled
 * @param {boolean} isClearDisabled - Whether the clear button is disabled
 * @param {function} onFilterChange - Callback when filter value changes (field, value)
 * @param {function} onApply - Callback when apply/filter button is clicked
 * @param {function} onClear - Callback when clear button is clicked
 * @param {string} variant - "popup" or "mini"
 * @param {function} onClose - Callback to close panel (mini variant only)
 */
const ShareholderFilters = observer(function ShareholderFilters({
  filters,
  validationMessages,
  showOnSubmitErrorState,
  isFilterDisabled,
  isClearDisabled,
  onFilterChange,
  onApply,
  onClear,
  variant,
  onClose
}) {
  const isPopup = variant === 'popup';

  const renderFilterFields = () => (
    <>
      <Input
        field={COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.SHAREHOLDER_SHARE_PERCENT}
        value={filters[COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.SHAREHOLDER_SHARE_PERCENT.NAME]}
        setFieldValue={onFilterChange}
        showOnSubmitErrorState={showOnSubmitErrorState}
        messages={validationMessages}
      />
      <CheckBox
        field={COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.ENTITIES_UNKNOWN_VALUE}
        value={filters[COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.ENTITIES_UNKNOWN_VALUE.NAME]}
        setFieldValue={onFilterChange}
        showOnSubmitErrorState={showOnSubmitErrorState}
        messages={validationMessages}
      />
      <CheckBox
        field={COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.INCLUDE_SUBSIDIARIES}
        value={filters[COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.INCLUDE_SUBSIDIARIES.NAME]}
        setFieldValue={onFilterChange}
        showOnSubmitErrorState={showOnSubmitErrorState}
        messages={validationMessages}
      />
      <CheckBox
        field={COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.EXPAND_GENERAL_PARTNERS}
        value={filters[COMPANY_DETAILS_SHAREHOLDER_POPUP_FIELDS.EXPAND_GENERAL_PARTNERS.NAME]}
        setFieldValue={onFilterChange}
        showOnSubmitErrorState={showOnSubmitErrorState}
        messages={validationMessages}
      />
    </>
  );

  const renderFilterActions = () => (
    <div className="filter-actions">
      <IconButton
        accessible
        innerText="Clear filter"
        onClick={onClear}
        disabled={isClearDisabled}
      />
      <IconButton
        accessible
        innerText={isPopup ? 'Apply' : 'Filter'}
        onClick={onApply}
        type={ICON_BUTTON_TYPES.BLUE}
        disabled={isFilterDisabled}
        filled
      />
    </div>
  );

  if (isPopup) {
    return (
      <div className="filters-wrap">
        <div className="filters-title">Filters</div>
        {renderFilterFields()}
        {renderFilterActions()}
      </div>
    );
  }

  // Mini variant
  return (
    <div className="filters-wrap" id="mini-structure-filters-wrap">
      <div className="fiter-header">
        <div className="header-title">Filters</div>
        <IconButton
          accessible
          ariaLabel="Close filters"
          icon={ICON_BUTTON_ICONS.X_LG}
          onClick={onClose}
        />
      </div>
      {renderFilterFields()}
      {renderFilterActions()}
    </div>
  );
});

export default ShareholderFilters;
